import React, { useEffect, useState } from 'react';
import { Button, Modal, Radio, Typography } from 'antd';
import SpinInputNumber from './SpinInputNumber';
import { VideoCameraOutlined } from '@ant-design/icons';
import { RenderSettings, FRAME_OFFSET } from '../store/useAppStore';

interface RenderModalProps {
  open: boolean;
  onClose: () => void;
  defaultSettings: RenderSettings;
  fps: number;
  totalFrames: number;
  onRender: (
    settings: RenderSettings,
    onProgress: (done: number, total: number, status?: 'rendering' | 'converting') => void,
    signal?: AbortSignal,
  ) => Promise<void>;
}

type BgMode = 'black' | 'white' | 'transparent';
type OutputFormat = 'png-sequence' | 'mp4';

const BG_MODES: { key: BgMode; label: string; tip: string }[] = [
  { key: 'black',       label: '⬛', tip: 'Black background' },
  { key: 'white',       label: '⬜', tip: 'White background' },
  { key: 'transparent', label: '▦',  tip: 'Transparent (alpha channel, PNG sequence only)' },
];

const OUTPUT_FORMATS: { key: OutputFormat; label: string; ext: string }[] = [
  { key: 'png-sequence', label: 'PNG Sequence', ext: '.########.png' },
  { key: 'mp4',          label: 'Video (MP4)',  ext: '.mp4' },
];

const { Text } = Typography;

export default function RenderModal({
  open,
  onClose,
  defaultSettings,
  fps,
  totalFrames,
  onRender,
}: RenderModalProps) {
  const [settings, setSettings] = useState<RenderSettings>(defaultSettings);
  const [rendering, setRendering] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; status?: 'rendering' | 'converting' } | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  useEffect(() => {
    if (open) {
      setSettings(defaultSettings);
      setProgress(null);
      setRendering(false);
    }
  // defaultSettings is intentionally excluded: effect should only reset when the modal opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const update = (patch: Partial<RenderSettings>) =>
    setSettings((s) => ({ ...s, ...patch }));

  const clamped = {
    startFrame: Math.max(0, Math.min(settings.startFrame, totalFrames - 1)),
    endFrame:   Math.max(0, Math.min(settings.endFrame,   totalFrames - 1)),
    stepFrames: Math.max(1, settings.stepFrames),
  };
  const frameCount = clamped.endFrame >= clamped.startFrame
    ? Math.floor((clamped.endFrame - clamped.startFrame) / clamped.stepFrames) + 1
    : 0;
  const durationSecs = fps > 0 ? (frameCount / fps).toFixed(2) : '0.00';

  const handleRender = async () => {
    const finalSettings: RenderSettings = { ...settings, ...clamped };
    const ctrl = new AbortController();
    setAbortController(ctrl);
    setRendering(true);
    setProgress({ done: 0, total: frameCount, status: 'rendering' });
    try {
      await onRender(finalSettings, (done, total, status) => 
        setProgress({ done, total, status: status || 'rendering' })
      , ctrl.signal);
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('Render error:', err);
      }
    } finally {
      setRendering(false);
      setAbortController(null);
    }
  };

  const handleCancel = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
  };

  const btnBase: React.CSSProperties = {
    padding: '3px 9px',
    fontSize: 13,
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#555',
    cursor: 'pointer',
    background: 'transparent',
    color: '#888',
    userSelect: 'none',
  };
  const btnActive: React.CSSProperties = {
    ...btnBase,
    background: '#1d3461',
    color: '#ddeeff',
    borderColor: '#558cf4',
  };

  const isBrowserSupported = typeof (window as any).showDirectoryPicker === 'function';

  return (
    <Modal
      title="Export"
      open={open}
      onCancel={!rendering ? onClose : undefined}
      closable={!rendering}
      mask={{ closable: !rendering }}
      footer={null}
      width={500}
    >
      {/* ── Format ─────────────────────────────────────────────── */}
      <section style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Format</Text>
        <div style={{ display: 'flex', gap: 6 }}>
          {OUTPUT_FORMATS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => !rendering && update({ outputFormat: key })}
              disabled={rendering}
              style={settings.outputFormat === key ? btnActive : btnBase}
            >
              {label}
            </button>
          ))}
        </div>
        {settings.outputFormat === 'mp4' && (
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            H.264 MP4 via FFmpeg.wasm. First render: FFmpeg loads (~30MB) — may take 30–60 seconds.
            Subsequent renders use cached FFmpeg. Transparent background converts to black.
          </Text>
        )}
      </section>
      {/* ── Output Name ────────────────────────────────────────────── */}
      <section style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Output Name</Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <input
            value={settings.outputName}
            onChange={(e) => update({ outputName: e.target.value })}
            placeholder="output_name"
            disabled={rendering}
            style={{
              flex: 1,
              padding: '4px 8px',
              border: '1px solid #d9d9d9',
              borderRight: 'none',
              borderRadius: '6px 0 0 6px',
              fontSize: 14,
              lineHeight: '22px',
              outline: 'none',
            }}
          />
          <span style={{
            padding: '4px 10px',
            background: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: '0 6px 6px 0',
            fontSize: 14,
            color: '#888',
            whiteSpace: 'nowrap',
          }}>
            {settings.outputFormat === 'mp4' ? '.mp4' : '.########.png'}
          </span>
        </div>
        <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
          {settings.outputFormat === 'mp4'
            ? <code>{(settings.outputName || 'output').replace(/[\/\\:*?"<>|]/g, '_')}.mp4</code>
            : <><code>{(settings.outputName || 'output').replace(/[\/\\:*?"<>|]/g, '_')}.{String(FRAME_OFFSET).padStart(8, '0')}.png</code></>
          }
        </Text>
      </section>

      {/* ── Background ─────────────────────────────────────────────── */}
      <section style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Background</Text>
        <div style={{ display: 'flex', gap: 6 }}>
          {BG_MODES.map(({ key, label, tip }) => (
            <button
              key={key}
              onClick={() => !rendering && update({ bgMode: key })}
              disabled={rendering}
              style={settings.bgMode === key ? btnActive : btnBase}
              title={tip}
            >
              {label}
            </button>
          ))}
        </div>
        {settings.bgMode === 'transparent' && (
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            Outputs RGBA PNG — alpha=0 where no paint, alpha=255 where fully painted. RGB channels are black in empty areas.
          </Text>
        )}
      </section>

      {/* ── Frame Range ────────────────────────────────────────────── */}
      <section style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Frame Range</Text>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Start</Text>
            <SpinInputNumber
              min={FRAME_OFFSET} max={totalFrames - 1 + FRAME_OFFSET} step={1} precision={0}
              value={settings.startFrame + FRAME_OFFSET}
              onChange={(v) => update({ startFrame: (v ?? FRAME_OFFSET) - FRAME_OFFSET })}
              disabled={rendering}
              style={{ width: 80 }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>End</Text>
            <SpinInputNumber
              min={FRAME_OFFSET} max={totalFrames - 1 + FRAME_OFFSET} step={1} precision={0}
              value={settings.endFrame + FRAME_OFFSET}
              onChange={(v) => update({ endFrame: (v ?? totalFrames - 1 + FRAME_OFFSET) - FRAME_OFFSET })}
              disabled={rendering}
              style={{ width: 80 }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Every Nth frame</Text>
            <SpinInputNumber
              min={1} max={totalFrames} step={1} precision={0}
              value={settings.stepFrames}
              onChange={(v) => update({ stepFrames: v ?? 1 })}
              disabled={rendering}
              style={{ width: 70 }}
            />
          </label>
        </div>
        <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
          {frameCount} frame{frameCount !== 1 ? 's' : ''} @ {fps} fps ≈ {durationSecs}s
        </Text>
      </section>

      {/* ── Progress bar ───────────────────────────────────────────── */}
      {progress !== null && (
        <section style={{ marginBottom: 20 }}>
          <div style={{
            background: '#1a1a2e',
            borderRadius: 4,
            height: 10,
            overflow: 'hidden',
            marginBottom: 4,
          }}>
            <div style={{
              height: '100%',
              width: `${
                progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0
              }%`,
              background: progress.status === 'converting' ? '#ff7a45' : '#558cf4',
              transition: 'width 0.12s linear',
              animation: progress.status === 'converting' ? 'pulse 1.5s ease-in-out infinite' : 'none',
            }} />
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {progress.status === 'converting'
              ? `Converting to MP4… ${Math.round((progress.done / progress.total) * 100)}%`
              : `${progress.done} / ${progress.total} frames${progress.done === progress.total && progress.total > 0 ? ' — Complete!' : rendering ? ' — Rendering…' : ''}`}
          </Text>
        </section>
      )}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>

      {/* ── Browser support warnings ─────────────────────────────────── */}
      {!isBrowserSupported && settings.outputFormat === 'png-sequence' && (
        <section style={{ marginBottom: 16 }}>
          <Text type="warning" style={{ fontSize: 11 }}>
            ⚠ Your browser does not support the File System Access API.
            Each PNG will be downloaded individually.
          </Text>
        </section>
      )}


      {/* ── Render button ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          type="primary"
          icon={<VideoCameraOutlined />}
          loading={rendering && progress?.status !== 'converting'}
          disabled={frameCount === 0 || (rendering && progress?.status === 'converting')}
          onClick={handleRender}
          style={{ flex: 1 }}
        >
          {rendering
            ? 'Exporting…'
            : settings.outputFormat === 'mp4'
              ? `Export Video (${frameCount} frame${frameCount !== 1 ? 's' : ''} ≈ ${durationSecs}s @ ${fps} fps)`
              : `Render ${frameCount} Frame${frameCount !== 1 ? 's' : ''}`}
        </Button>
        {rendering && progress && (
          <Button 
            onClick={handleCancel} 
            danger
          >
            Cancel
          </Button>
        )}
      </div>
    </Modal>
  );
}
